import { Head, router, usePage } from "@inertiajs/react"
import { Clock, MessageSquareMore } from "lucide-react"
import { useState } from "react"

import { ChatHistorySheet } from "@/components/ask/chat-history-sheet"
import { ChatInput } from "@/components/ask/chat-input"
import { SuggestionCards } from "@/components/ask/suggestion-cards"
import { OnboardingEmptyState } from "@/components/onboarding-empty-state"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import AppLayout from "@/layouts/app-layout"
import { askPath, conversationsPath } from "@/routes"
import type { BreadcrumbItem, SuggestionCard } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "New session",
    href: askPath(),
  },
]

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return "Good morning"
  if (hour < 18) return "Good afternoon"
  return "Good evening"
}

interface AskProps {
  suggestion_cards?: SuggestionCard[]
  user_first_name?: string
}

export default function Ask({
  suggestion_cards = [],
  user_first_name = "",
}: AskProps) {
  const greeting = getGreeting()
  const { account } = usePage<{ account?: { state?: string } }>().props
  const isOnboarding = account?.state === "onboarding"
  const [historyOpen, setHistoryOpen] = useState(false)

  function handleCardSelect(card: SuggestionCard) {
    const data: Record<string, string> = { message: card.prompt }
    if (card.agent_slug) data.agent_slug = card.agent_slug
    router.post(conversationsPath(), data)
  }

  function handleSubmit(message: string, files?: File[]) {
    if (files && files.length > 0) {
      const formData = new FormData()
      formData.append("message", message)
      files.forEach((file) => formData.append("files[]", file))
      router.post(conversationsPath(), formData)
    } else {
      router.post(conversationsPath(), { message })
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="New session" />

      <div className="absolute top-4 right-4 z-10">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setHistoryOpen(true)}
              aria-label="Chat history"
            >
              <Clock className="size-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Chat history</TooltipContent>
        </Tooltip>
      </div>

      <ChatHistorySheet open={historyOpen} onOpenChange={setHistoryOpen} />

      <div className="flex flex-1 flex-col items-center justify-center px-4 pb-24">
        <div className="w-full max-w-2xl">
          {isOnboarding ? (
            <OnboardingEmptyState
              icon={MessageSquareMore}
              title="Your workspace is being configured"
              description="Imperative is working on configuring your agents. Once they're ready, you'll be able to engage with them here."
              className="flex-initial justify-start pt-16"
            />
          ) : (
            <>
              <div className="mb-12 text-center">
                <h1 className="text-2xl font-semibold tracking-tight">
                  {greeting}, {user_first_name}
                </h1>
                <p className="text-muted-foreground mt-1">
                  What can I help you with today?
                </p>
              </div>

              <SuggestionCards
                cards={suggestion_cards}
                onSelect={handleCardSelect}
              />
            </>
          )}

          <div className="mt-4 mb-4">
            <ChatInput onSubmit={handleSubmit} disabled={isOnboarding} />
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
